import { call, put, takeLatest } from 'redux-saga/effects';

import { Visitor, VisitorPhone, VisitorService } from '../../../../../entities/Visitor';
import {
  getVisitorsSuccess,
  getVisitorsError,
  getVisitorPhonesSuccess,
  getVisitorPhonesError,
  getVisitorPhonesRequest,
  getVisitorsRequest,
  getVisitorPhoneDelete,
  getVisitorPhoneAdd,
  getVisitorDelete,
  getVisitorsAddOrUpdate,
  getVisitorPhoneLatest,
} from './actions';
import {
  GET_VISITORS_REQUEST,
  GET_VISITOR_DELETE,
  GET_VISITOR_PHONES_REQUEST,
  GET_VISITOR_PHONE_ADD,
  GET_VISITOR_PHONE_DELETE,
  GET_VISITOR_ADD_OR_UPDATE,
  GET_VISITOR_PHONE_LATEST,
} from './constants';

const formatVisitorPhones = (phones: VisitorPhone[]): VisitorPhone[] =>
  phones.map((p) => {
    p.number = p.number.replace(/(^\d{2})(\d{3})(\d{3})(\d{2})(\d{2}$)/g, '$1 ($2) $3-$4-$5');
    return p;
  });

function* createEventSaga(action: ReturnType<typeof getVisitorsRequest>) {
  try {
    const { fio, number } = action.payload;
    const visitors: Visitor[] = yield call(VisitorService.getVisitors, fio, number);
    yield put(getVisitorPhonesSuccess([]));
    yield put(getVisitorsSuccess(visitors));
  } catch (error: any) {
    yield put(getVisitorsError(error));
  }
}

function* createVisitorAddOrUpdateEventSaga(action: ReturnType<typeof getVisitorsAddOrUpdate>) {
  try {
    const { visitor, fio, number } = action.payload;
    yield call(VisitorService.postVisitor, visitor);
    const visitors: Visitor[] = yield call(VisitorService.getVisitors, fio, number);
    yield put(getVisitorsSuccess(visitors));
  } catch (error: any) {
    yield put(getVisitorsError(error));
  }
}

function* createNumberDeleteEventSaga(action: ReturnType<typeof getVisitorPhoneDelete>) {
  try {
    const { numberId, visitorId } = action.payload;
    yield call(VisitorService.getVisitorPhoneDelete, numberId);
    const phones: VisitorPhone[] = yield call(VisitorService.getVisitorPhones, visitorId);
    yield put(getVisitorPhonesSuccess(formatVisitorPhones(phones)));
  } catch (error: any) {
    yield put(getVisitorsError(error));
  }
}

function* createNumberLatestEventSaga(action: ReturnType<typeof getVisitorPhoneLatest>) {
  try {
    const { numberId, visitorId } = action.payload;
    yield call(VisitorService.getVisitorPhoneLatest, numberId);
    const phones: VisitorPhone[] = yield call(VisitorService.getVisitorPhones, visitorId);
    yield put(getVisitorPhonesSuccess(formatVisitorPhones(phones)));
  } catch (error: any) {
    yield put(getVisitorsError(error));
  }
}

function* createVisitorDeleteEventSaga(action: ReturnType<typeof getVisitorDelete>) {
  try {
    const { visitorId, fio, number } = action.payload;
    yield call(VisitorService.getVisitorDelete, visitorId);
    const visitors: Visitor[] = yield call(VisitorService.getVisitors, fio, number);
    yield put(getVisitorsSuccess(visitors));
  } catch (error: any) {
    yield put(getVisitorsError(error));
  }
}

function* createNumberAddEventSaga(action: ReturnType<typeof getVisitorPhoneAdd>) {
  try {
    const { visitorId, data } = action.payload;
    yield call(VisitorService.getVisitorPhoneAdd, data);
    const phones: VisitorPhone[] = yield call(VisitorService.getVisitorPhones, visitorId);
    yield put(getVisitorPhonesSuccess(formatVisitorPhones(phones)));
  } catch (error: any) {
    yield put(getVisitorsError(error));
  }
}

function* createPhonesEventSaga(action: ReturnType<typeof getVisitorPhonesRequest>) {
  try {
    const { visitorId } = action.payload;
    if (visitorId > 0) {
      const phones: VisitorPhone[] = yield call(VisitorService.getVisitorPhones, visitorId);
      yield put(getVisitorPhonesSuccess(formatVisitorPhones(phones)));
    } else {
      yield put(getVisitorPhonesSuccess([]));
    }
  } catch (error: any) {
    yield put(getVisitorPhonesError(error));
  }
}

export function* visitorsTabSagaWatcher() {
  yield takeLatest(GET_VISITORS_REQUEST, createEventSaga);
}

export function* visitorPhonesTabSagaWatcher() {
  yield takeLatest(GET_VISITOR_PHONES_REQUEST, createPhonesEventSaga);
}

export function* visitorPhoneDeleteSagaWatcher() {
  yield takeLatest(GET_VISITOR_PHONE_DELETE, createNumberDeleteEventSaga);
}

export function* visitorPhoneLatestSagaWatcher() {
  yield takeLatest(GET_VISITOR_PHONE_LATEST, createNumberLatestEventSaga);
}

export function* visitorDeleteSagaWatcher() {
  yield takeLatest(GET_VISITOR_DELETE, createVisitorDeleteEventSaga);
}

export function* visitorPhoneAddSagaWatcher() {
  yield takeLatest(GET_VISITOR_PHONE_ADD, createNumberAddEventSaga);
}

export function* visitorAddOrUpdateSagaWatcher() {
  yield takeLatest(GET_VISITOR_ADD_OR_UPDATE, createVisitorAddOrUpdateEventSaga);
}
